import React from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import { setMyMagicLinkRequirement, setMyTwoFactorMethod } from './magicLinkSettingsApi';
import '../styles/settings.css';

export default function MagicLinkToggle({ value, method = 'email', disabled, loading = false, onChange, onMethodChange }) {
  const { showToast } = useNotification();
  const [saving, setSaving] = React.useState(false);
  const [savingMethod, setSavingMethod] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleToggle = async (e) => {
    const next = Boolean(e.target.checked);
    setError('');
    setSaving(true);
    try {
      await setMyMagicLinkRequirement({ requireMagicLink: next });
      onChange?.(next);
      showToast(next ? '2FA enabled.' : '2FA disabled.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to update setting';
      setError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputId = 'requireMagicLinkToggle';
  const methodId = 'twoFactorMethodSelect';

  const handleMethodChange = async (e) => {
    const nextMethod = e.target.value === 'sms' ? 'sms' : 'email';
    setError('');
    setSavingMethod(true);
    try {
      await setMyTwoFactorMethod({ method: nextMethod });
      onMethodChange?.(nextMethod);
      showToast(
        nextMethod === 'sms' ? '2FA delivery set to SMS login code.' : '2FA delivery set to email login link.',
        'success'
      );
    } catch (err) {
      const message = err?.message || 'Failed to update 2FA method';
      setError(message);
      showToast(message, 'error');
    } finally {
      setSavingMethod(false);
    }
  };

  return (
    <>
      {error ? (
        <div className="alert alertError" role="alert" aria-live="polite" style={{ marginBottom: 10 }}>
          {error}
        </div>
      ) : null}

      {!loading && !Boolean(value) ? (
        <div className="alert alertError securityWarn" role="status" aria-live="polite" style={{ marginBottom: 10 }}>
          <span className="securityWarnIcon" aria-hidden="true">!</span>
          <span>2FA is currently disabled. Enable it to better protect your admin account from unauthorized access.</span>
        </div>
      ) : null}

      <div className="settingsRow" aria-busy={saving ? 'true' : 'false'}>
        <div>
          <p className="settingsRowTitle">Require 2FA on login</p>
          <p className="settingsRowSub">When enabled, signing in requires an email link or SMS code after password.</p>
        </div>

        <label className="switch" htmlFor={inputId} aria-label="Require 2FA on login">
          <input
            id={inputId}
            type="checkbox"
            checked={Boolean(value)}
            onChange={handleToggle}
            disabled={Boolean(disabled) || saving}
          />
          <span className="switchTrack">
            <span className="switchThumb" />
          </span>
        </label>
      </div>

      {Boolean(value) ? (
        <div className="settingsRow" aria-busy={savingMethod ? 'true' : 'false'}>
          <div>
            <p className="settingsRowTitle">2FA delivery method</p>
            <p className="settingsRowSub">Choose whether your second factor arrives by email link or SMS code.</p>
          </div>
          <select
            id={methodId}
            className="input"
            value={method === 'sms' ? 'sms' : 'email'}
            onChange={handleMethodChange}
            disabled={Boolean(disabled) || savingMethod}
            aria-label="2FA delivery method"
            style={{ maxWidth: 220 }}
          >
            <option value="email">Email login link</option>
            <option value="sms">SMS login code</option>
          </select>
        </div>
      ) : null}
    </>
  );
}

